import { getAttributeDataFromPatientProfileData } from '@components/hooks/PatientTrialApplySurveyProvider/getAttributeDataFromPatientProfileData'
import { type FlagOrExperiment } from '@lib/experimentService/consts'
import { isExperimentEnabledByCookieOrEppo } from '@lib/experimentService/isExperimentEnabledByCookieOrEppo'
import {
  type PatientApplyProfileData,
  isNotEmptyOrSkippedOrNoneOrNotSureAnswer,
} from '@modules/patientApplyProfile/helpers/PatientApplyProfileData'
import type { PatientTrialApplySurveyAttribute } from './types'

type BaseArgumentsForShouldSkipQuestionForPatient = {
  attribute: PatientTrialApplySurveyAttribute
  parentQuestions?: { id: string; validValues?: string[] }[]
  patientProfileData: PatientApplyProfileData
}

type ArgumentsForShouldSkipQuestionForPatientWithFeatureFlag =
  BaseArgumentsForShouldSkipQuestionForPatient & {
    featureFlagNameForQuestion: FlagOrExperiment
    trialApplyIdTokenOrSegmentAnonymousId: string
  }

type ArgumentsForShouldSkipQuestionForPatientWithoutFeatureFlag =
  BaseArgumentsForShouldSkipQuestionForPatient & {
    featureFlagNameForQuestion?: never
    trialApplyIdTokenOrSegmentAnonymousId?: never
  }

type ArgumentsForShouldSkipQuestionForPatient =
  | ArgumentsForShouldSkipQuestionForPatientWithFeatureFlag
  | ArgumentsForShouldSkipQuestionForPatientWithoutFeatureFlag

/**
 * Determines if a question in the conditions section of the apply flow should be hidden from the patient.
 *
 * @param attribute Optional attribute to check against patient profile data to see if it has been previously answered
 * @param featureFlagNameForQuestion Optional feature flag, when `false` the question will be skipped, else will fallback to the default behaviour
 * @param parentQuestionName Optional name of the parent question to check against patient profile data
 * @param patientProfileData Structured profile data entries for the patient
 * @param validParentQuestionValues List of valid values from parent question in order to show this step
 *
 * @returns boolean indicating if the question should be skipped
 */
export async function shouldSkipQuestionForPatient({
  attribute,
  featureFlagNameForQuestion,
  parentQuestions = [],
  patientProfileData,
  trialApplyIdTokenOrSegmentAnonymousId,
}: ArgumentsForShouldSkipQuestionForPatient) {
  // If the feature flag is disabled, always skip the question
  // Else, fallback to default logic
  if (featureFlagNameForQuestion) {
    const questionIsOkayToShowToPatient =
      await isExperimentEnabledByCookieOrEppo({
        defaultExperimentEnabled: false,
        flagOrExperimentKey: featureFlagNameForQuestion,
        subjectKey: trialApplyIdTokenOrSegmentAnonymousId,
      })

    if (!questionIsOkayToShowToPatient) {
      return true
    }
  }

  if (parentQuestions.length > 0) {
    // Note: this implies an OR relationship (i.e. if any parent has a valid response then show the step)
    let hasValidParentQuestion = false

    for (const parentQuestion of parentQuestions) {
      const parentQuestionData = patientProfileData
        .filter((item) => item.conceptName === parentQuestion.id)
        .map((entry) => entry.conceptValue)
        .filter(isNotEmptyOrSkippedOrNoneOrNotSureAnswer)

      if (parentQuestionData.length === 0) {
        continue
      }

      const { validValues = [] } = parentQuestion

      if (
        validValues.length === 0 ||
        parentQuestionData.some((value) =>
          validValues.map((v) => v.toLowerCase()).includes(value.toLowerCase()),
        )
      ) {
        hasValidParentQuestion = true
        break
      }
    }

    if (!hasValidParentQuestion) {
      return true
    }
  }

  return attributeIsPresentInPatientProfile({
    attribute,
    patientProfileData,
    skippable: true,
  })
}

/**
 * Does the attribute (e.g. name, DoB) exist in the patient profile data?
 * @property {PatientTrialApplySurveyAttribute} attribute - Attribute to check for
 * @property {PatientApplyProfileData} patientProfileData - Patient profile to search in
 * @returns {boolean} Whether the attribute is present in the patient profile data
 */
function attributeIsPresentInPatientProfile({
  attribute,
  patientProfileData,
  skippable,
}: {
  attribute: PatientTrialApplySurveyAttribute
  patientProfileData: PatientApplyProfileData
  skippable?: boolean
}) {
  const attributeValue = getAttributeDataFromPatientProfileData({
    attribute,
    patientProfileData,
  })
  const attributeValueIsPresent = attributeValue.length > 0

  if (skippable) {
    return attributeValueIsPresent
  }

  return (
    attributeValueIsPresent &&
    attributeValue.every((v) => v && v.toString().trim().length > 0)
  )
}
